import React, { useState } from 'react';
import {
  Flex,
  Icon,
  Button,
  FormControl,
  NumberInput,
  NumberInputField,
  Textarea,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import {
  MdCalendarToday,
  MdClose,
  MdDelete,
  MdFormatAlignLeft,
  MdLocationOn,
  MdPeople,
} from 'react-icons/md';
import ReactDatePicker from 'react-datepicker';
import { RiPriceTag2Line } from 'react-icons/ri';
import styled from 'styled-components';

import { FaDiceD6 } from 'react-icons/fa';
import { UseMutateFunction } from 'react-query';
import { useAuthContext } from '../AuthProvider';
import { Schedule } from '../models/schedule';
import TextInput from './TextInput';
import InputLabel from './InputLabel';
import SearchModal from './SearchModal';
import { Group } from '../models/group';
import BoardGameItem from './BoardGameItem';
import { toTimestamp } from '../utils/timestamp';
import Header from './Header';

const DatePicker = styled(ReactDatePicker)`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  font-size: 14px;
  line-height: 100%;
  padding: 16px;
`;

interface ScheduleFormProps {
  group: Group;
  addSchedule: UseMutateFunction<void, unknown, Omit<Schedule, 'id'>, unknown>;
}

const ScheduleForm = ({ group, addSchedule }: ScheduleFormProps) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { register, control, handleSubmit } = useForm();
  const [modal, setModal] = useState(false);
  const [boardGames, setBoardGames] = useState<string[]>([]);

  const handleClick = handleSubmit(async (data) => {
    const schedule = {
      name: data.name,
      description: data.description,
      location: data.location,
      start: toTimestamp(data.start),
      total: Number(data.total),
      owner: user?.uid!,
      members: [user?.uid!],
      boardGames,
    };
    await addSchedule(schedule);
    navigate(-1);
  });

  return (
    <Flex flexDir="column" width="100%">
      <Flex
        pos="relative"
        h="100%"
        flexDirection="column"
        align="center"
        px="1rem"
      >
        <Header title="일정 생성" rightIcon={MdClose} rightTo={-1} />
        <TextInput
          icon={RiPriceTag2Line}
          label="일정 이름"
          required
          placeholder="일정 제목을 작성해주세요."
          register={register}
          fieldKey="name"
        />
        <FormControl m="28px">
          <InputLabel icon={MdCalendarToday} label="날짜 및 시간" required />
          <Controller
            control={control}
            name="start"
            render={({ field }) => (
              <DatePicker
                minDate={new Date()}
                startDate={new Date()}
                showTimeSelect
                popperPlacement="bottom-end"
                timeIntervals={30}
                timeCaption="시간"
                dateFormat="yyyy.MM.dd hh:mm"
                placeholderText="날짜 및 시간"
                onChange={(date) => field.onChange(date)}
                selected={field.value}
              />
            )}
          />
        </FormControl>

        <TextInput
          icon={MdLocationOn}
          label="일정 위치"
          required
          placeholder="일정 위치를 작성해주세요."
          register={register}
          fieldKey="location"
        />
        <Flex alignItems="center" justify="space-between" width="100%" m="28px">
          <InputLabel icon={MdPeople} label="인원" required />
          <Controller
            control={control}
            name="total"
            render={({ field }) => (
              <NumberInput
                focusBorderColor="#515151"
                p="1rem"
                value={field.value}
                onChange={field.onChange}
              >
                <NumberInputField />
              </NumberInput>
            )}
          />
        </Flex>
        <Flex flexDir="column" alignItems="start" width="100%">
          <InputLabel icon={MdFormatAlignLeft} label="일정 소개" required />
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                value={field.value}
                onChange={field.onChange}
                placeholder="일정에 대한 설명을 작성해주세요."
                focusBorderColor="#515151"
                p="1rem"
              />
            )}
          />
        </Flex>
        <Flex
          flexDir="column"
          width="100%"
          alignItems="start"
          m="28px"
          gap="12px"
        >
          <InputLabel
            icon={FaDiceD6}
            label="플레이할 보드게임"
            subLabel="최대 6개"
          />
          <Flex flexDir="column" gap="13px" width="100%">
            {boardGames.map((boardGame) => (
              <BoardGameItem
                key={boardGame}
                boardGameId={boardGame}
                actionButton={
                  <Icon
                    as={MdDelete}
                    color="#858585"
                    onClick={() =>
                      setBoardGames((prev) =>
                        prev.filter((bid) => bid !== boardGame)
                      )
                    }
                  />
                }
              />
            ))}
          </Flex>
          <Button
            alignSelf="stretch"
            bgColor="#F0F0F0"
            color="#858585"
            borderRadius="8px"
            py="18px"
            fontWeight="semibold"
            onClick={() => setModal(true)}
          >
            보드게임 선택
          </Button>
          {modal && (
            <SearchModal
              onRegister={(boardGameId) =>
                setBoardGames((prev) =>
                  prev.length < 6 ? [...prev, boardGameId] : prev
                )
              }
              isExist={(boardGameId) => group.boardGames.includes(boardGameId)}
              onClose={() => setModal(false)}
            />
          )}
        </Flex>
      </Flex>
      <Flex borderTop="1px solid #D9D9D9" pt="12px" pb="34px" px="1rem">
        <Button
          onClick={handleClick}
          width="100%"
          py="14px"
          borderRadius="40px"
          color="white"
          bgColor="#D84E46"
          _active={{
            bgColor: '#AB170F',
          }}
          _hover={{
            bgColor: '#AB170F',
          }}
        >
          생성하기
        </Button>
      </Flex>
    </Flex>
  );
};

export default ScheduleForm;
